// StaffPaymentTable.jsx
// ROOT FIX: Derive isAdmin directly from HeartConnect-StaffData.rolePermission.rolename
// This is always written fresh on login and is the single source of truth.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { MdLockOutline, MdLockOpen } from "react-icons/md";
import { FaRupeeSign } from "react-icons/fa";

import Pagination from "../../../../Components/Pagination/Pagination";
import ModalWrapper from "../../../../Components/Modal/ModalWrapper";
import SendMoneyModal from "./SendMoneyModal";
import DeductCoinsModal from "./DeductCoinsModal";
import { usePermission } from "Context/PermissionContext";
import { loadDecrypted } from "Common Usage/localStorageUtils";

import {
  StaffDropdown,
  SummaryCards,
  TransactionFilters,
  TransactionTable,
  TransactionMobileCards,
  formatDateFromString,
  parseDate,
} from "./StaffPaymentHelpers";

const BASE_URL = process.env.REACT_APP_API_URL;
const PER_PAGE = 10;

// ---------------------------------------------------------------------------
// ✅ Helper: read the full staff profile saved at login from localStorage.
//    Shape: { fullName, gender, phoneNumber, rolePermission: { rolename, ... }, ... }
// ---------------------------------------------------------------------------
const getStaffDataFromStorage = () => {
  try {
    const raw = loadDecrypted("HeartConnect-StaffData");
    // loadDecrypted already JSON.parses, but guard both cases
    if (!raw) return null;
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};

const ADMIN_ROLES = ["admin", "superadmin", "super admin", "super-admin"];

const resolveRoleFromStaffData = (staffData) => {
  const roleName =
    staffData?.rolePermission?.rolename?.toLowerCase()?.trim() ?? "";
  return {
    roleName,
    isAdmin: ADMIN_ROLES.includes(roleName),
  };
};

const StaffPaymentTable = () => {
  // ✅ Keep using PermissionContext only for its loading gate — it confirms the
  //    API round-trip is done and localStorage is now consistent for this session.
  const { loading: permissionsLoading } = usePermission();

  // ✅ Derive role from StaffData — written fresh on every login, never stale.
  const staffData = getStaffDataFromStorage();
  const { roleName, isAdmin } = resolveRoleFromStaffData(staffData);

  // Staff identity from storage
  const loggedInStaffId = loadDecrypted("HeartConnect-StaffId");
  const loggedInStaffName =
    staffData?.fullName || loadDecrypted("HeartConnect-StaffName") || "Staff";

  // Staff selection (admin only)
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedStaffMeta, setSelectedStaffMeta] = useState(null);

  // Data & pagination
  const [transactions, setTransactions] = useState([]);
  const [walletDetails, setWalletDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [txnTypeFilter, setTxnTypeFilter] = useState("all");
  const [walletStatusFilter, setWalletStatusFilter] = useState("all");

  // Frozen status map (admin only)
  const [staffFrozenMap, setStaffFrozenMap] = useState(new Map());

  // Modals
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [deductModal, setDeductModal] = useState({
    isOpen: false,
    staffId: null,
    staffName: "",
    currentBalance: 0,
  });

  // ---------------------------------------------------------------------------
  // Data fetching — ALL gated on permissionsLoading === false
  // This ensures the API has confirmed the session before any fetch runs.
  // ---------------------------------------------------------------------------
  const fetchAllWalletsStatus = useCallback(async () => {
    if (permissionsLoading || !isAdmin) return;
    try {
      const token = loadDecrypted("HeartConnect-Token");
      const { data } = await axios.get(`${BASE_URL}/staff-wallet/all-wallets`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      if (data.success) {
        const map = new Map();
        data.data.forEach((w) => {
          const sid = w.staffId?._id || w.staffId;
          if (sid) map.set(sid, { frozen: w.isFrozen ?? false });
        });
        setStaffFrozenMap(map);
      }
    } catch (err) {
      console.error("Failed to fetch wallets status", err);
    }
  }, [isAdmin, permissionsLoading]);

  const fetchHistory = useCallback(
    async (page = 1) => {
      if (permissionsLoading) return;

      const targetStaffId = isAdmin ? selectedStaffId : loggedInStaffId;
      if (!isAdmin && !targetStaffId) return;

      setLoading(true);
      try {
        const token = loadDecrypted("HeartConnect-Token");
        let url, params;

        if (isAdmin) {
          url = `${BASE_URL}/staff-wallet/get-wallet-history`;
          params = { page, limit: PER_PAGE };
          if (targetStaffId) params.staffId = targetStaffId;
        } else {
          url = `${BASE_URL}/staff-wallet/my-wallets-history/${targetStaffId}`;
          params = { page, limit: PER_PAGE };
        }

        const { data } = await axios.get(url, {
          params,
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        });

        if (data.success) {
          let enriched;
          if (isAdmin) {
            enriched = data.data.map((item) => {
              const { date, time } = formatDateFromString(item.createdAt);
              return {
                ...item,
                displayDate: date,
                displayTime: time,
                description: item.note || "Wallet Transaction",
                type: item.type.toLowerCase(),
              };
            });
          } else {
            enriched = data.data.map((item) => {
              const { date, time } = formatDateFromString(item.createdAt);
              const description =
                item.note ||
                (item.historyType === "RECHARGE"
                  ? "User Recharge"
                  : "Wallet Transaction");
              return {
                ...item,
                displayDate: date,
                displayTime: time,
                description,
                type: item.type
                  ? item.type.toLowerCase()
                  : item.historyType === "RECHARGE"
                  ? "credit"
                  : "debit",
              };
            });
          }
          setTransactions(enriched);
          setTotalPages(data.pagination.totalPages);
        } else {
          setTransactions([]);
          setTotalPages(1);
        }
      } catch (err) {
        console.error("Failed to fetch history", err);
        Swal.fire("Error", "Could not load transaction history", "error");
        setTransactions([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, selectedStaffId, loggedInStaffId, permissionsLoading]
  );

  const fetchWalletDetails = useCallback(async () => {
    if (permissionsLoading) return;

    const targetStaffId = isAdmin ? selectedStaffId : loggedInStaffId;
    if (!targetStaffId) {
      setWalletDetails(null);
      return;
    }
    try {
      const token = loadDecrypted("HeartConnect-Token");
      const { data } = await axios.get(
        `${BASE_URL}/staff-wallet/staffs-wallet-by-id/${targetStaffId}`,
        { headers: { ...(token && { Authorization: `Bearer ${token}` }) } }
      );
      if (data.success) {
        setWalletDetails({
          balance: data.data.balance ?? 0,
          frozen: data.data.isFrozen ?? false,
        });
      } else {
        setWalletDetails(null);
      }
    } catch (err) {
      console.error("Failed to fetch wallet details", err);
      setWalletDetails(null);
    }
  }, [isAdmin, selectedStaffId, loggedInStaffId, permissionsLoading]);

  useEffect(() => {
    fetchAllWalletsStatus();
  }, [fetchAllWalletsStatus]);

  useEffect(() => {
    fetchHistory(currentPage);
    fetchWalletDetails();
  }, [fetchHistory, fetchWalletDetails, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStaffId]);

  // ---------------------------------------------------------------------------
  // Client-side filtering — unchanged
  // ---------------------------------------------------------------------------
  const filteredTransactions = useMemo(() => {
    const q = search.toLowerCase();
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    let filtered = [...transactions];

    if (!isAdmin && txnTypeFilter !== "all") {
      filtered = filtered.filter((tx) => tx.type === txnTypeFilter);
    }

    if (isAdmin && walletStatusFilter !== "all") {
      const targetStatus = walletStatusFilter === "frozen";
      filtered = filtered.filter((tx) => {
        const staffIdForTx = tx.staffId?._id || tx.staffId;
        if (!staffIdForTx) return false;
        const isFrozen = staffFrozenMap.get(staffIdForTx)?.frozen ?? false;
        return isFrozen === targetStatus;
      });
    }

    if (q) {
      filtered = filtered.filter(
        (tx) =>
          tx.description?.toLowerCase().includes(q) ||
          (tx.txnId && tx.txnId.toLowerCase().includes(q)) ||
          (tx.staffId?.fullName &&
            tx.staffId.fullName.toLowerCase().includes(q))
      );
    }

    if (dateFilter !== "all") {
      filtered = filtered.filter((tx) => {
        const txDate = parseDate(tx.displayDate);
        if (!txDate) return false;
        if (dateFilter === "this_week") return txDate >= startOfWeek;
        if (dateFilter === "this_month") return txDate >= startOfMonth;
        if (dateFilter === "custom") {
          let ok = true;
          if (fromDate) ok = txDate >= new Date(fromDate);
          if (toDate) ok = ok && txDate <= new Date(toDate + "T23:59:59");
          return ok;
        }
        return true;
      });
    }

    return filtered;
  }, [
    transactions,
    search,
    dateFilter,
    fromDate,
    toDate,
    txnTypeFilter,
    walletStatusFilter,
    isAdmin,
    staffFrozenMap,
  ]);

  const todayStr = new Date().toLocaleDateString("en-IN");
  const todayTransactions = filteredTransactions.filter(
    (tx) => tx.displayDate === todayStr
  );
  const todayTotalCount = todayTransactions.length;
  const todayTotalCredit = todayTransactions
    .filter((tx) => tx.type === "credit")
    .reduce((sum, tx) => sum + (tx.amount || 0), 0);
  const todayTotalDebit = todayTransactions
    .filter((tx) => tx.type === "debit")
    .reduce((sum, tx) => sum + (tx.amount || 0), 0);

  const resetPage = () => setCurrentPage(1);
  const clearFilters = () => {
    setSearch("");
    setDateFilter("all");
    setFromDate("");
    setToDate("");
    if (!isAdmin) setTxnTypeFilter("all");
    if (isAdmin) setWalletStatusFilter("all");
    resetPage();
  };

  // ---------------------------------------------------------------------------
  // Action handlers — unchanged
  // ---------------------------------------------------------------------------
  const handleToggleFreeze = async () => {
    const targetStaffId = isAdmin ? selectedStaffId : loggedInStaffId;
    if (!targetStaffId) {
      Swal.fire("Info", "No staff selected", "info");
      return;
    }
    const willFreeze = !walletDetails?.frozen;
    const actionLabel = willFreeze ? "Freeze" : "Unfreeze";
    const confirm = await Swal.fire({
      title: `${actionLabel} Wallet?`,
      html: `<div>${actionLabel} <strong>${
        isAdmin ? selectedStaffMeta?.fullName || "staff" : loggedInStaffName
      }</strong>'s wallet?<br/>${
        willFreeze
          ? "No transactions will be allowed while frozen."
          : "Transactions will be re-enabled."
      }</div>`,
      icon: willFreeze ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionLabel}!`,
      confirmButtonColor: "#c51369",
    });
    if (!confirm.isConfirmed) return;
    try {
      const token = loadDecrypted("HeartConnect-Token");
      const endpoint = willFreeze ? "freeze-wallet" : "unfreeze-wallet";
      const { data } = await axios.put(
        `${BASE_URL}/staff-wallet/${endpoint}/${targetStaffId}`,
        {},
        { headers: { ...(token && { Authorization: `Bearer ${token}` }) } }
      );
      if (!data.success) throw new Error(data.message);
      Swal.fire("Success", `Wallet ${actionLabel.toLowerCase()}d`, "success");
      fetchWalletDetails();
      fetchAllWalletsStatus();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
  };

  const handleOpenDeduct = () => {
    const targetStaffId = isAdmin ? selectedStaffId : loggedInStaffId;
    if (!targetStaffId) {
      Swal.fire("Info", "No staff selected", "info");
      return;
    }
    setDeductModal({
      isOpen: true,
      staffId: targetStaffId,
      staffName: isAdmin
        ? selectedStaffMeta?.fullName || ""
        : loggedInStaffName,
      currentBalance: walletDetails?.balance || 0,
    });
  };

  const handleDeductSuccess = () => {
    setDeductModal((p) => ({ ...p, isOpen: false }));
    fetchWalletDetails();
    fetchHistory(currentPage);
    fetchAllWalletsStatus();
  };

  const handleTransactionSuccess = () => {
    setIsSendModalOpen(false);
    fetchWalletDetails();
    fetchHistory(currentPage);
    fetchAllWalletsStatus();
  };

  // ---------------------------------------------------------------------------
  // ✅ Loading skeleton — shown while PermissionContext confirms the session
  // ---------------------------------------------------------------------------
  if (permissionsLoading) {
    return (
      <div className="mx-auto max-w-7xl">
        <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-10 w-48 rounded-lg bg-gray-200" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-gray-200" />
              ))}
            </div>
            <div className="h-12 rounded-lg bg-gray-200" />
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 rounded bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="mx-auto max-w-7xl">
      <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
        {/* ✅ Debug strip — remove before final deployment */}
        {/* {process.env.NODE_ENV === "development" && (
          <div className="mb-4 rounded-lg border border-dashed border-yellow-400 bg-yellow-50 px-4 py-2 text-xs text-yellow-800">
            🔍 Role from StaffData: <strong>{roleName || "not found"}</strong>
            {" · "}isAdmin: <strong>{String(isAdmin)}</strong>
            {" · "}StaffId: <strong>{loggedInStaffId || "none"}</strong>
          </div>
        )} */}

        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-1 flex-wrap items-end gap-4">
            {isAdmin && (
              <StaffDropdown
                value={selectedStaffId}
                onChange={(id, staffObj) => {
                  setSelectedStaffId(id);
                  setSelectedStaffMeta(staffObj);
                }}
                onClear={() => {
                  setSelectedStaffId(null);
                  setSelectedStaffMeta(null);
                }}
              />
            )}

            {walletDetails && (
              <div className="rounded-xl border border-pink-100 bg-pink-50 px-4 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#c51369]">
                  Current Coin Balance
                </p>
                <p className="text-xl font-bold text-gray-800">
                  {walletDetails.balance.toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-gray-500">
                  {walletDetails.frozen ? "❄️ Frozen" : "✓ Active"}
                </p>
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setIsSendModalOpen(true)}
                className="rounded-full bg-[#c51369] px-5 py-2 text-sm font-semibold text-white shadow hover:bg-[#a8105a]"
              >
                + Add Coin Amount
              </button>
              <button
                onClick={handleOpenDeduct}
                className="rounded-full border border-amber-500 bg-amber-50 px-5 py-2 text-sm font-semibold text-amber-600 hover:bg-amber-100"
              >
                <FaRupeeSign className="mr-1 inline" size={14} /> Deduct Coin
                Amount
              </button>
              <button
                onClick={handleToggleFreeze}
                className={`rounded-full border px-5 py-2 text-sm font-semibold ${
                  walletDetails?.frozen
                    ? "border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100"
                    : "border-purple-500 bg-purple-50 text-purple-600 hover:bg-purple-100"
                }`}
              >
                {walletDetails?.frozen ? (
                  <>
                    <MdLockOpen className="mr-1 inline" size={14} /> Unfreeze
                    Wallet
                  </>
                ) : (
                  <>
                    <MdLockOutline className="mr-1 inline" size={14} /> Freeze
                    Wallet
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <SummaryCards
          isAdmin={isAdmin}
          todayTotalCount={todayTotalCount}
          todayTotalCredit={todayTotalCredit}
          todayTotalDebit={todayTotalDebit}
        />

        <TransactionFilters
          isAdmin={isAdmin}
          search={search}
          setSearch={setSearch}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          txnTypeFilter={txnTypeFilter}
          setTxnTypeFilter={setTxnTypeFilter}
          walletStatusFilter={walletStatusFilter}
          setWalletStatusFilter={setWalletStatusFilter}
          onResetPage={resetPage}
          onClearFilters={clearFilters}
        />

        <TransactionTable
          loading={loading}
          filteredTransactions={filteredTransactions}
          currentPage={currentPage}
          PER_PAGE={PER_PAGE}
        />

        <TransactionMobileCards
          loading={loading}
          filteredTransactions={filteredTransactions}
          currentPage={currentPage}
          PER_PAGE={PER_PAGE}
        />

        {totalPages > 1 && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              showFirstLast={true}
            />
          </div>
        )}
      </div>

      <ModalWrapper
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
      >
        <SendMoneyModal
          isOpen={isSendModalOpen}
          onClose={() => setIsSendModalOpen(false)}
          onTransaction={handleTransactionSuccess}
        />
      </ModalWrapper>
      <DeductCoinsModal
        isOpen={deductModal.isOpen}
        onClose={() => setDeductModal((p) => ({ ...p, isOpen: false }))}
        staffId={deductModal.staffId}
        staffName={deductModal.staffName}
        currentBalance={deductModal.currentBalance}
        onSuccess={handleDeductSuccess}
      />
    </div>
  );
};

export default StaffPaymentTable;
